import { Procedure } from "@medplum/fhirtypes";
import { PROCEDURE_TYPE_RANGES } from "../procedure/procedure-type";

/**
 * Get imaging procedure ranges (procedures with modality field defined)
 */
export const IMAGING_PROCEDURE_RANGES = PROCEDURE_TYPE_RANGES.filter(
  (range): range is typeof range & { modality: string } => "modality" in range && !!range.modality
);

/**
 * Get the imaging modality category from a single procedure's CPT code
 * @param procedure
 * @returns The imaging modality category or "-"
 */
export function getImagingModalityCategory(procedure: Procedure): string {
  for (const c of procedure.code?.coding ?? []) {
    if (c.system !== "http://www.ama-assn.org/go/cpt" || !c.code) continue;
    const foundCategory = getCategoryFromCptCodeRange(c.code);
    if (foundCategory) return foundCategory;
  }
  return "-";
}

function getCategoryFromCptCodeRange(cptCode: string): string | undefined {
  const numericCode = parseInt(cptCode, 10);
  if (isNaN(numericCode)) return undefined;
  const match = IMAGING_PROCEDURE_RANGES.find(r => numericCode >= r.start && numericCode <= r.end);
  return match?.category;
}

/**
 * Extract imaging modality from multiple procedures by analyzing CPT codes
 * @param procedures Array of FHIR Procedure resources
 * @returns Comma-separated string of unique modalities, or "-" if none found
 */
export function getImagingModalityFromProcedures(procedures: Procedure[]): string {
  const modalities = new Set<string>();

  procedures.forEach(procedure => {
    // place category in a modality column as it is more descriptive when filtering!
    const category = getImagingModalityCategory(procedure);
    if (category && category !== "-") {
      modalities.add(category);
    }
  });

  return modalities.size > 0 ? Array.from(modalities).join(", ") : "-";
}

/**
 * Check if a CPT code is in the imaging range
 * @param cptCode CPT code string
 * @returns true if the code is an imaging procedure
 */
export function isImagingCptCode(cptCode: string): boolean {
  const numericCode = parseInt(cptCode, 10);
  if (isNaN(numericCode)) return false;
  return IMAGING_PROCEDURE_RANGES.some(
    range => numericCode >= range.start && numericCode <= range.end
  );
}
